/**
 * Smart barge-in for OpenAI Realtime voice agents (phone + browser).
 *
 * With plain server VAD every sound on the line — a TV, a door, someone in the
 * room, a cough — cancels the agent mid-sentence and triggers a fresh reply,
 * so it stops and repeats itself. Sessions therefore run with
 * `interrupt_response: false, create_response: false` and this gate decides:
 *
 *   - every caller turn is held until its transcript arrives (≤ 1.5 s when
 *     the agent is silent): noise, phantom lines ("Thank you.", subtitles
 *     credits) and text in a script the call can't be in are deleted, so the
 *     model never answers words it imagined from a TV or the line;
 *   - agent talking → real words interrupt (cancel + new reply), "haan / ok /
 *     hmm" acknowledgements and noise do not;
 *   - someone else in the room: their words transcribe perfectly, so text can't
 *     tell them from the caller. Loudness can — the gate learns how loud the
 *     caller is and drops turns well below that (see MicLevelMeter).
 *
 * Pure logic over an IO adapter, shared by the worker (phone) and the web
 * client (WebRTC).
 */
import { romanizeIndic } from './romanize.js';
import { phoneDigitsNote } from './voice-digits.js';
/** Recent mic loudness. Feed it RMS (0..1) every few tens of ms. */
export class MicLevelMeter {
    frames = [];
    add(rms, at = Date.now()) {
        this.frames.push({ at, db: 20 * Math.log10(Math.max(rms, 1e-5)) });
        if (this.frames.length > 1500)
            this.frames.splice(0, 300);
    }
    /** 90th percentile: the speech in the window, not the pauses around it. */
    level(fromMs, toMs) {
        const dbs = this.frames.filter((f) => f.at >= fromMs && f.at <= toMs).map((f) => f.db).sort((a, b) => a - b);
        if (dbs.length < 3)
            return null;
        return dbs[Math.floor((dbs.length - 1) * 0.9)];
    }
}
const BACKCHANNEL = new Set([
    'hmm', 'hm', 'hmmm', 'mm', 'mmm', 'mhm', 'uh', 'um', 'uhh', 'umm', 'ah', 'aha', 'oh', 'ohh',
    'ok', 'okay', 'okey', 'yes', 'yeah', 'yep', 'ya', 'yah', 'right', 'sure', 'fine', 'alright', 'huh',
    'haan', 'han', 'haa', 'haaan', 'ha', 'ji', 'jee', 'accha', 'acha', 'achha', 'achcha', 'theek', 'thik', 'hai', 'ho',
    'हाँ', 'हां', 'हा', 'जी', 'अच्छा', 'ठीक', 'है', 'हम्म', 'हम', 'ओके', 'हाँजी', 'हांजी',
]);
// Whisper-family "hallucinations" on silence / line noise.
const PHANTOMS = new Set([
    'you', 'thank you', 'thanks', 'thanks for watching', 'thank you for watching', 'bye', 'bye bye',
    'please subscribe', 'subtitles by the amaraorg community', 'so', 'the', 'a', 'i', 'oh', 'uh', 'um',
    'thank you very much', 'thanks for listening', 'see you next time', 'like and subscribe',
    'ご視聴ありがとうございました', '字幕', 'продолжение следует', 'субтитры', 'спасибо за просмотр',
    '请不吝点赞 订阅 转发 打赏支持明镜与点点栏目', 'mbc 뉴스', '감사합니다', 'धन्यवाद',
]);
/** Scripts a call in this language can produce (others = noise). null = don't check. */
export function expectedScripts(language) {
    const l = (language || '').toLowerCase();
    if (!l || l === 'auto' || l === 'en' || l === 'hi' || l === 'hinglish')
        return /[A-Za-z\u0900-\u097F]/;
    if (['bn', 'pa', 'gu', 'or', 'ta', 'te', 'kn', 'ml', 'mr', 'as', 'ne'].includes(l))
        return /[A-Za-z\u0900-\u0D7F]/;
    if (['es', 'fr', 'de', 'it', 'pt', 'nl', 'id', 'ms', 'tl', 'sw', 'tr', 'pl', 'ro', 'vi'].includes(l))
        return /[A-Za-zÀ-ɏ]/;
    return null;
}
// Non-speech sounds the transcriber writes out as words ("khansi" = cough).
const SOUND_WORDS = new Set([
    'cough', 'coughs', 'coughing', 'khansi', 'khaansi', 'khasi', 'khaasi', 'khans', 'sneeze', 'sneezes', 'sneezing', 'chheenk', 'chhink', 'chink',
    'laugh', 'laughs', 'laughing', 'laughter', 'haha', 'hahaha', 'hehe', 'sigh', 'sighs', 'sighing', 'breath', 'breathing', 'breathes', 'gasp',
    'music', 'noise', 'noises', 'static', 'silence', 'inaudible', 'unintelligible', 'applause', 'ahem', 'beep', 'beeps', 'ringing', 'ring', 'knock', 'knocking',
    'background', 'clears', 'clearing', 'throat', 'crying', 'cries', 'sniff', 'sniffs', 'yawn', 'yawns', 'click', 'clicks', 'typing', 'footsteps',
    'blank_audio', 'speaking', 'foreign', 'language', 'the', 'and', 'a', 'of', 'in', 'on',
]);
const SOUND_CORE = new Set([...SOUND_WORDS].filter((w) => !['background', 'speaking', 'foreign', 'language', 'the', 'and', 'a', 'of', 'in', 'on', 'clears', 'clearing', 'throat'].includes(w)));
// What an assistant (or a YouTube outro) says — the transcriber invents these
// from noise; a caller does not say "how may I help you" to the agent.
const ASSISTANT_LIKE = [
    /\b(main|mai|mein|me)\s+(aapki|apki|aap ki|ap ki|aapka|apka)\s+(kaise|kya|aur|kis tarah)?\s*(madad|sahayata|help)\s+(kar|kr)\s+(sakta|sakti|sakte)/,
    /\bkya\s+(main|mai|mein|me)\s+(aapki|apki)?\s*(kuchh|kuch)?\s*(aur)?\s*(madad|sahayata|help)/,
    /\bkripya\s+(apna|aapna)\s+(nam|naam)\s+(bataen|bataye|batayen|bataiye|batayein)/,
    /\b(subscribe|like|share)\s+(karen|kare|karein|kijiye)\b/,
    /\bhow\s+(can|may)\s+i\s+(help|assist)\s+you\b/,
    /\b(is there )?anything else i can (help|do)\b/,
    /\bthanks?\s+(you\s+)?for\s+(calling|watching|listening)\b/,
    // A caller doesn't ask the agent for *its* order / account.
    /\b(aapka|apka|aapki|apki)\s+(order|ordar|account|akaunt|khata)\b/,
    /\bplease\s+(like|subscribe)\b/,
    /\b(dhanyavad|dhanyawad|dhanyavaad)\s+(dekhne|sunne)\b/,
];
export function normaliseLine(text) {
    return text.toLowerCase().replace(/[^\p{L}\p{N}\p{M}\s]/gu, ' ').replace(/\s+/g, ' ').trim();
}
/**
 * Classify a caller line heard while the agent was talking. `botText` = what
 * the agent was saying (a line that merely repeats it is echo, not the caller).
 */
export function classifyCallerLine(text, botText = '', scripts = null) {
    // [coughing], (laughs), *sighs*, ♪ … — sound descriptions, not words.
    if (/^\s*[[(*♪♫].*[\])*♪♫]\s*$/u.test(text))
        return 'noise';
    const t = normaliseLine(text);
    if (!t || PHANTOMS.has(t))
        return 'noise';
    const latin = normaliseLine(romanizeIndic(text));
    if (PHANTOMS.has(latin))
        return 'noise';
    const latinWords = latin.split(' ').filter(Boolean);
    if (latinWords.length && latinWords.length <= 4 && latinWords.every((w) => SOUND_WORDS.has(w)) && latinWords.some((w) => SOUND_CORE.has(w)))
        return 'noise';
    if (ASSISTANT_LIKE.some((re) => re.test(latin)))
        return 'noise';
    // e.g. Korean / Russian / Japanese on a Hindi-English call: invented from noise.
    if (scripts && /\p{L}/u.test(t)) {
        const letters = [...t].filter((c) => /\p{L}/u.test(c));
        const ok = letters.filter((c) => scripts.test(c)).length;
        if (ok / letters.length < 0.5)
            return 'noise';
    }
    const words = t.split(' ');
    if (words.length === 1 && [...words[0]].length < 2)
        return 'noise';
    const bot = normaliseLine(botText);
    if (bot && t.length >= 8 && bot.includes(t))
        return 'noise';
    // Overlapping turn that mostly repeats what the agent was saying = its own echo.
    if (bot && latinWords.length >= 6) {
        const botWords = new Set(normaliseLine(romanizeIndic(botText)).split(' '));
        const shared = latinWords.filter((w) => botWords.has(w)).length;
        if (shared / latinWords.length >= 0.75)
            return 'noise';
    }
    if (words.length <= 3 && words.every((w) => BACKCHANNEL.has(w)))
        return 'backchannel';
    return 'speech';
}
/** An acknowledgement this close to the end of the agent's turn is an answer. */
const TAIL_MS = 1500;
/** Give up waiting for a transcript after this long (agent talking / silent). */
const TRANSCRIPT_WAIT_MS = 4000;
const QUIET_WAIT_MS = 1500;
/** A cancelled reply normally reports response.done at once; don't wait for it longer than this. */
const CANCEL_WAIT_MS = 800;
/** A turn this far below the caller's own level is someone else (agent talking / silent). */
const FAR_OVERLAP_DB = 8;
const FAR_QUIET_DB = 12;
/** Far turns in a row before the agent asks the caller to speak closer, and how much the caller level is then relaxed. */
const FAR_STREAK_ASK = 3;
const FAR_STREAK_RELAX_DB = 6;
const FAR_VOICE_NOTE = '[System note, not said by the caller] Only faint background voices were heard, not the caller. Ignore anything you think you heard from them. Say one short sentence in the current language: you cannot hear the caller clearly, please speak closer to the phone. Then wait.';
/** Look back this far before speech_started: the event arrives after the speech began. */
const LEVEL_LOOKBACK_MS = 500;
export class BargeInGate {
    io;
    now;
    responding = false;
    held = new Map();
    /** Turns that started while the agent was talking (only those can be echo). */
    overlapped = new Set();
    ackAt = 0;
    followTimer = null;
    botText = '';
    ducked = false;
    scripts = expectedScripts('auto');
    speechAt = 0;
    turnLevel = new Map();
    /** How loud the caller is; learned from turns taken while the agent was silent. */
    callerDb = null;
    /** Far turns dropped in a row while the agent was silent. */
    farStreak = 0;
    /** The current turn began while the agent was talking (it may end after the agent). */
    speechOverBot = false;
    replyAfterCancel = null;
    /** Digits of a number the caller is still saying. */
    partialDigits = '';
    /** Replies the agent finished (response.done) — lets a hang-up wait for the goodbye. */
    responsesDone = 0;
    /** How many times the caller said real words. */
    callerSpeechCount = 0;
    constructor(io, now = Date.now) {
        this.io = io;
        this.now = now;
    }
    /** The call's language (snapshot agent.language) — limits accepted scripts. */
    setLanguage(language) {
        this.scripts = expectedScripts(language);
    }
    get botSpeaking() {
        return this.responding || this.io.isPlaying();
    }
    /** Every response.create we send goes through here. */
    requestResponse() {
        this.responding = true;
        this.io.respond();
    }
    responseCreated() {
        this.responding = true;
        this.botText = '';
    }
    botDelta(delta) {
        this.botText += delta;
    }
    responseDone() {
        this.responding = false;
        this.responsesDone++;
        if (this.replyAfterCancel) {
            clearTimeout(this.replyAfterCancel);
            this.replyAfterCancel = null;
            this.requestResponse();
        }
        this.armFollowUp();
    }
    speechStarted() {
        this.speechAt = this.now();
        this.speechOverBot = this.botSpeaking;
        if (!this.botSpeaking)
            return;
        // Don't turn the agent down for a voice in the background.
        const level = this.io.micLevel?.(this.speechAt - LEVEL_LOOKBACK_MS, this.speechAt) ?? null;
        if (level === null || this.callerDb === null || level >= this.callerDb - FAR_OVERLAP_DB)
            this.duck(true);
    }
    speechStopped() {
        // keep ducked until the transcript decides
    }
    /** input_audio_buffer.committed — a caller turn ended. */
    committed(itemId) {
        // Decide once we know what was said — also when the agent is silent, or a
        // TV / other voices get an answer to words the model imagined.
        const wait = this.botSpeaking ? TRANSCRIPT_WAIT_MS : QUIET_WAIT_MS;
        if (this.botSpeaking || this.speechOverBot)
            this.overlapped.add(itemId);
        this.speechOverBot = false;
        const now = this.now();
        const level = this.io.micLevel?.((this.speechAt || now - 2000) - LEVEL_LOOKBACK_MS, now) ?? null;
        this.speechAt = 0;
        if (level !== null)
            this.turnLevel.set(itemId, level);
        this.held.set(itemId, setTimeout(() => this.transcript(itemId, null), wait));
    }
    /** Caller transcript (null = transcription failed / timed out). Returns how it was classified, null if the turn was already decided. */
    transcript(itemId, text) {
        const timer = this.held.get(itemId);
        if (timer === undefined)
            return null;
        clearTimeout(timer);
        this.held.delete(itemId);
        const overlapped = this.overlapped.delete(itemId);
        const levelDb = this.turnLevel.get(itemId) ?? null;
        this.turnLevel.delete(itemId);
        let kind = text === null ? 'backchannel' : classifyCallerLine(text, overlapped ? this.botText : '', this.scripts);
        const far = kind !== 'noise' && this.isFar(levelDb, overlapped);
        if (far)
            kind = 'noise';
        else if (kind !== 'noise')
            this.learnCallerLevel(levelDb, overlapped, text);
        this.io.trace?.({ kind, far, levelDb, callerDb: this.callerDb, overlapped, text });
        if (this.held.size === 0)
            this.duck(false);
        if (kind === 'speech') {
            this.callerSpeechCount++;
            this.io.onCallerSpeech?.();
            if (text) {
                const { note, partial } = phoneDigitsNote(text, this.partialDigits);
                this.partialDigits = partial;
                if (note)
                    this.io.note?.(note);
            }
            const cancelling = this.responding;
            if (this.botSpeaking)
                this.io.interrupt();
            this.ackAt = 0;
            // response.create while the cancelled reply is still active is rejected,
            // which left the agent silent: wait for its response.done.
            if (cancelling)
                this.replyOnceCancelled();
            else
                this.requestResponse();
            return kind;
        }
        if (kind === 'noise') {
            this.io.drop(itemId);
            if (far && !overlapped)
                this.askToComeCloser();
            return kind;
        }
        // Acknowledgement: keep it in the history; answer it only if the agent has
        // already finished (or was about to) — otherwise it was just "hmm, go on".
        if (!this.botSpeaking) {
            this.requestResponse();
            return kind;
        }
        this.ackAt = this.now();
        this.armFollowUp();
        return kind;
    }
    isFar(levelDb, overlapped) {
        if (levelDb === null || this.callerDb === null)
            return false;
        return levelDb < this.callerDb - (overlapped ? FAR_OVERLAP_DB : FAR_QUIET_DB);
    }
    learnCallerLevel(levelDb, overlapped, text) {
        if (!overlapped)
            this.farStreak = 0;
        if (levelDb === null || text === null)
            return;
        if (this.callerDb === null)
            this.callerDb = levelDb;
        else if (!overlapped)
            this.callerDb = Math.max(levelDb, this.callerDb - 1);
    }
    /**
     * Only far voices for a while with the agent waiting: a chatty room, or the
     * caller moved away from the mic. Their words are never answered (that would
     * let the room talk to the agent), but the caller must not be left unheard.
     */
    askToComeCloser() {
        if (++this.farStreak < FAR_STREAK_ASK || this.botSpeaking)
            return;
        this.farStreak = 0;
        this.callerDb -= FAR_STREAK_RELAX_DB;
        this.io.note?.(FAR_VOICE_NOTE);
        this.requestResponse();
    }
    replyOnceCancelled() {
        if (this.replyAfterCancel)
            return;
        this.replyAfterCancel = setTimeout(() => {
            this.replyAfterCancel = null;
            this.requestResponse();
        }, CANCEL_WAIT_MS);
    }
    /**
     * After end_call: resolve true once the agent's goodbye has been spoken
     * (a reply finished and playback stopped), false if the caller said real
     * words meanwhile — then keep the call going. Gives up (true) after maxMs.
     */
    waitForGoodbye(maxMs = 12_000) {
        const startedAt = this.now();
        const doneAtStart = this.responsesDone;
        const speechAtStart = this.callerSpeechCount;
        let quietSince = 0;
        return new Promise((resolve) => {
            const t = setInterval(() => {
                const now = this.now();
                if (this.callerSpeechCount > speechAtStart) {
                    clearInterval(t);
                    resolve(false);
                    return;
                }
                if (now - startedAt > maxMs) {
                    clearInterval(t);
                    resolve(true);
                    return;
                }
                const quiet = !this.botSpeaking && this.held.size === 0 && this.responsesDone > doneAtStart;
                if (!quiet) {
                    quietSince = 0;
                    return;
                }
                if (!quietSince)
                    quietSince = now;
                // A short pause after the goodbye so the last word isn't clipped.
                if (now - quietSince >= 700) {
                    clearInterval(t);
                    resolve(true);
                }
            }, 100);
        });
    }
    dispose() {
        for (const t of this.held.values())
            clearTimeout(t);
        this.held.clear();
        this.overlapped.clear();
        this.turnLevel.clear();
        if (this.replyAfterCancel)
            clearTimeout(this.replyAfterCancel);
        this.replyAfterCancel = null;
        if (this.followTimer)
            clearInterval(this.followTimer);
        this.followTimer = null;
    }
    armFollowUp() {
        if (!this.ackAt || this.followTimer)
            return;
        this.followTimer = setInterval(() => {
            if (this.botSpeaking)
                return;
            if (this.followTimer)
                clearInterval(this.followTimer);
            this.followTimer = null;
            const at = this.ackAt;
            this.ackAt = 0;
            if (at && this.now() - at <= TAIL_MS && this.held.size === 0)
                this.requestResponse();
        }, 150);
    }
    duck(on) {
        if (this.ducked === on)
            return;
        this.ducked = on;
        this.io.duck?.(on);
    }
}
//# sourceMappingURL=voice-barge-in.js.map